import React from "react";
import CalciteChip from "../../../../shared/CalciteChip";

// Helper function to format time without seconds
const formatTime = (date) => {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatArrayOrString = (value) => {
  if (!value) return "";
  return Array.isArray(value) ? value.join(", ") : value;
};

const normalizeTimezone = (timezone) => {
  if (timezone) {
    return timezone
    .replace(/EST|EDT/g, "ET")
    .replace(/PST|PDT/g, "PT");
  } else return '';

};

function decodeHTMLEntities(str = "") {
  return new DOMParser()
    .parseFromString(str, "text/html")
    .documentElement.textContent;
}

const getEventLocation = (eventInfo) => {

  const engagementType = eventInfo.engagementType?.toLowerCase();

  if (engagementType === "virtual") {
    return "Virtual";
  }

  const { eventVenue, city, state, country } = eventInfo;
  const isUS = country?.toLowerCase() === "united states";

  // Build location parts, filtering out any empty/undefined values
  const regionParts = isUS
    ? [city, state].filter(Boolean)
    : [city, state, country].filter(Boolean);

  const regionStr = regionParts.join(", ");

  let location;
  if (eventVenue && regionStr) {
    location = `${eventVenue} | ${regionStr}`;
  } else {
    location = eventVenue || regionStr || "";
  }

  if (engagementType === "hybrid") {
    return location ? `${location} and Virtual` : "Virtual";
  }

  return location;
};

//check if events are on the same day
const isSameDay = (start, end) => {
  return start.getFullYear() === end.getFullYear() &&
         start.getMonth() === end.getMonth() &&
         start.getDate() === end.getDate();
};

// format date in a consistent en-US style, independent of country
const formatDate = (start, country, isMultiDay = false, end = null) => {
  const startMonth = start.toLocaleDateString("en-US", { month: "short" });
  const startDay = start.getDate();
  const year = start.getFullYear();

  if (isMultiDay) {
    const endDay = end.getDate();
    // If the event spans multiple months, include the end month
    if (start.getMonth() !== end.getMonth()) {
      const endMonth = end.toLocaleDateString("en-US", { month: "short" });
      return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
    }
    return `${startMonth} ${startDay}–${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay}, ${year}`;
};

//format event dates and times
const formatEventDates = (startStr, endStr, startTimeStr, endTimeStr, country) => {
  const start = new Date(startStr);
  const end = new Date(endStr);
  const startTime = startTimeStr ? new Date(startTimeStr) : null;
  const endTime = endTimeStr ? new Date(endTimeStr) : null;

  // Get and normalize timezone
  const tzRaw = start.toLocaleTimeString("en-US", { timeZoneName: "short" });
  const timezone = normalizeTimezone(tzRaw.split(" ").pop());

  if (isSameDay(start, end)) {
    // Single-day event
    const dateStr = formatDate(start, country);
    const timeRange = [startTime, endTime]
      .filter(Boolean)
      .map(formatTime)
      .join("–");

    return (
      <p className="event-schedule">
        {dateStr}
        {timeRange && <span>{` ${timeRange} ${timezone}`.trim()}</span>}
      </p>
    );
  } else {
    // Multi-day event
    const dateStr = formatDate(start, country, true, end);

    return <p className="event-schedule">{dateStr}</p>;
  }
};

//Link target based on hostname
const getLinkTarget = (url) => {
  try {
    return new URL(url).hostname === window.location.hostname ? "_self" : "_blank";
  } catch {
    return "_blank"; // Fallback for invalid URLs
  }
};

export default function EventsCard(props) {
  if (props.app !== "events") return null;

  const eventInfo = props.cardinfo;
  const country = eventInfo.country?.toLowerCase() || "";

  const pill = (
    <CalciteChip
      className={`eventtype-chip eventtype-chip-${eventInfo.eventType}`}
      scale="s"
      appearance="outline"
      kind="neutral"
    >
      {eventInfo.eventType}
    </CalciteChip>
  );

  //Format event data
  const name = formatArrayOrString(eventInfo.eventName);
  const location = getEventLocation(eventInfo);
  const formattedEventSchedule = formatEventDates(
    eventInfo.eventStartDate,
    eventInfo.eventEndDate,
    eventInfo.eventStartTime,
    eventInfo.eventEndTime,
    country
  );

  const analyticsProps = {
    "data-event": "track-event",
    "data-component-name": "events-card",
    "data-component-link": "event title",
    "data-component-link-type": "card",
  };

  return (
    <article className={`card-event-block lighttheme ${props.app}-card`}>
      <div className="card card-wide">
        <div className="card-content card-content-padding">
          <div className={`${props.app}-top`}>
            {pill}
          </div>

          <div className={`${props.app}-middle`}>
            <div className={`${props.app}-left`}>
              <a
                className={`avenir-regular esri-font-2 ${props.app}-link`}
                href={eventInfo.link}
                target={getLinkTarget(eventInfo.link)}
                {...analyticsProps}
              >
                <span>{decodeHTMLEntities(name)}</span>
              </a>
              <p className={`avenir-regular esri-font-0 ${props.app}-bottomText`}>
                <calcite-icon icon="pin-tear" scale="s" appearance="solid" aria-label="Location" role="img"/> {location}
              </p>
            </div>
            <div className={`${props.app}-right`}>
              {formattedEventSchedule}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
